import { useEffect, useRef, useState } from 'react'
import { Plus, X } from 'lucide-react'
import { fetchAnnotations, createAnnotation, deleteAnnotation, type Annotation } from '../api/annotations'
import type { Period } from '../landing'
import { brand } from '../theme'
import { InfoTip } from './InfoTip'

// Olof [meeting 29:50–33:28]: explain a peak/dip on the trend — "vecka 22 kampanj Velo 319 kr",
// a payday week, a holiday — so the next meeting doesn't re-litigate "what happened week 22?".
// A per-profile, persistent note strip under the trend: everyone viewing this profile sees them.

const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const fmtDay = (d: string): string => {
  const [y, m, day] = d.split('-')
  return `${Number(day)} ${MON[Number(m) - 1] ?? m} ${y}`
}
const fmtRange = (from: string, to: string): string => {
  if (from === to) return fmtDay(from)
  // Same month+year → "10–16 Aug 2026"; else spell both ends out.
  if (from.slice(0, 7) === to.slice(0, 7)) return `${Number(from.slice(8))}–${fmtDay(to)}`
  return `${fmtDay(from)} – ${fmtDay(to)}`
}

const S: Record<string, React.CSSProperties> = {
  wrap: { marginTop: 12, borderTop: `1px solid ${brand.grey}`, paddingTop: 10 },
  head: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 },
  title: { fontSize: 12.5, fontWeight: 700, color: brand.greyText, letterSpacing: 0.2 },
  add: { display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 600, color: brand.teal, background: 'none', border: 'none', cursor: 'pointer', padding: 4 },
  empty: { fontSize: 12, color: brand.greyText, fontStyle: 'italic', padding: '2px 0 4px' },
  item: { display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 12.5, padding: '5px 0', borderBottom: `1px solid ${brand.grey}22` },
  when: { flex: '0 0 auto', color: brand.tealDark, fontWeight: 700, fontVariantNumeric: 'tabular-nums', minWidth: 96 },
  note: { flex: '1 1 auto', color: brand.text },
  by: { color: brand.greyText, fontSize: 11 },
  del: { flex: '0 0 auto', background: 'none', border: 'none', color: brand.greyText, cursor: 'pointer', padding: 2, lineHeight: 1 },
  form: { display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', margin: '6px 0 4px' },
  input: { fontFamily: brand.font, fontSize: 12.5, padding: '5px 8px', border: `1px solid ${brand.grey}`, borderRadius: 6, color: brand.text, background: 'var(--surface)' },
  save: { fontSize: 12.5, fontWeight: 700, color: '#fff', background: brand.teal, border: 'none', borderRadius: 6, padding: '6px 12px', cursor: 'pointer' },
  cancel: { fontSize: 12.5, color: brand.greyText, background: 'none', border: 'none', cursor: 'pointer', padding: '6px 8px' },
  err: { fontSize: 12, color: '#c0392b', marginTop: 4 },
}

/**
 * `openAt` opens the form with a range already filled in — the curve sends it when a point is
 * clicked (MP-OS-136), so "what happened in week 35?" is answered where the question is asked
 * instead of by retyping two dates. `nonce` lets the same bucket be clicked again after the
 * form was cancelled; without it a repeat click on the same point would look dead.
 *
 * `onLoaded` reports the notes upward so the trend charts can mark the buckets that carry one.
 * The fetch stays here because this is the component that also writes them.
 */
export function TrendNotes({
  period,
  openAt,
  onLoaded,
}: {
  period: Period | null
  openAt?: { from: string; to: string; nonce: number } | null
  onLoaded?: (notes: Annotation[]) => void
}) {
  const [notes, setNotes] = useState<Annotation[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [text, setText] = useState('')
  const [busy, setBusy] = useState(false)
  const saving = useRef(false)

  const reload = () => {
    setError(null)
    return fetchAnnotations()
      .then((n) => {
        setNotes(n)
        onLoaded?.(n)
      })
      .catch((e) => setError(e.message))
  }
  useEffect(() => {
    reload()
  }, [])

  useEffect(() => {
    if (!openAt) return
    setFrom(openAt.from)
    setTo(openAt.to)
    setText('')
    setError(null)
    setAdding(true)
  }, [openAt?.nonce])

  const openForm = () => {
    // Default the range to the selected period so a note lands where the user is looking.
    setFrom(period?.from ?? '')
    setTo(period?.to ?? '')
    setText('')
    setError(null)
    setAdding(true)
  }

  const save = async () => {
    if (saving.current) return
    if (!from || !text.trim()) {
      setError('Pick a start date and write a note.')
      return
    }
    if (to && to < from) {
      setError('The end date must be on or after the start date.')
      return
    }
    saving.current = true
    setBusy(true)
    setError(null)
    try {
      await createAnnotation({ date_from: from, date_to: to || from, note: text.trim() })
      setAdding(false)
      await reload()
    } catch (e) {
      setError((e as Error).message)
    } finally {
      saving.current = false
      setBusy(false)
    }
  }

  const remove = async (id: number) => {
    setError(null)
    try {
      await deleteAnnotation(id)
      setNotes((cur) => {
        const next = cur ? cur.filter((n) => n.id !== id) : cur
        if (next) onLoaded?.(next) // else the marker stays on the curve after the note is gone
        return next
      })
    } catch (e) {
      setError((e as Error).message)
    }
  }

  const sorted = notes ? [...notes].sort((a, b) => (a.date_from < b.date_from ? 1 : -1)) : null

  return (
    <div style={S.wrap}>
      <div style={S.head}>
        <span style={S.title}>
          NOTES ON THE TREND
          <InfoTip label="About trend notes" size="small">
            Add a note to explain a peak or dip, such as a campaign, a payday week or a holiday.
          </InfoTip>
        </span>
        {!adding && (
          <button style={S.add} onClick={openForm} title="Explain a peak or dip — a campaign, a payday week, a holiday">
            <Plus size={13} /> Add note
          </button>
        )}
      </div>

      {adding && (
        <div style={S.form}>
          <input type="date" aria-label="From" value={from} onChange={(e) => setFrom(e.target.value)} style={S.input} />
          <span style={{ color: brand.greyText }}>→</span>
          <input type="date" aria-label="To (optional)" value={to} onChange={(e) => setTo(e.target.value)} style={S.input} />
          <input
            type="text"
            aria-label="Note"
            placeholder="e.g. Velo campaign · 319 kr"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && save()}
            style={{ ...S.input, flex: '1 1 200px', minWidth: 160 }}
          />
          <button style={S.save} onClick={save} disabled={busy}>
            {busy ? 'Saving…' : 'Save'}
          </button>
          <button style={S.cancel} onClick={() => setAdding(false)} disabled={busy}>
            Cancel
          </button>
        </div>
      )}

      {error && <div style={S.err} role="alert">{error}{notes === null && !adding && <button onClick={reload}>Try again</button>}</div>}

      {sorted && sorted.length === 0 && !adding && (
        <div style={S.empty}>No notes yet.</div>
      )}

      {sorted?.map((n) => (
        <div key={n.id} style={S.item}>
          <span style={S.when}>{fmtRange(n.date_from, n.date_to)}</span>
          <span style={S.note}>
            {n.note}
            {n.created_by && <span style={S.by}> · {n.created_by}</span>}
          </span>
          <button style={S.del} onClick={() => remove(n.id)} title="Remove note" aria-label="Remove note">
            <X size={13} />
          </button>
        </div>
      ))}
    </div>
  )
}
